import { For, Show, Suspense, createSignal } from "solid-js";
import { useParams, A, createAsync, useAction, type RouteDefinition } from "@solidjs/router";
import { Title } from "@solidjs/meta";
import { AppShell } from "~/components/layout";
import {
  Button, Badge, Card, CardContent, Skeleton, EmptyState,
  Textarea, ScrollArea, Separator, Input, Breadcrumbs
} from "~/components/ui";
import { 
  getDossier, getVideos, 
  addDossierSection, removeFromDossier 
} from "~/server/bff";
import { AddToDossierModal, NewAnalysisModal, GenerateReportModal } from "~/components/modals";
import { formatRelativeTime } from "~/lib/utils";
import {
  ArrowLeft, Edit, FileText, BarChart3, Download, Plus,
  GripVertical, Trash2, ExternalLink, MessageSquare, Check, X
} from "lucide-solid";

export const route = {
  preload: ({ params }) => Promise.all([getDossier(params.id as string), getVideos()]),
} satisfies RouteDefinition;

export default function DossierDetailPage() {
  const params = useParams();
  const [activeSection, setActiveSection] = createSignal<string | null>(null);
  const [notes, setNotes] = createSignal("");
  const [isEditing, setIsEditing] = createSignal(false);
  const [showAddSection, setShowAddSection] = createSignal(false);
  const [newSectionName, setNewSectionName] = createSignal("");
  const [showAddItemModal, setShowAddItemModal] = createSignal(false);
  const [showAnalysisModal, setShowAnalysisModal] = createSignal(false);
  const [showReportModal, setShowReportModal] = createSignal(false);
  const [actionLoading, setActionLoading] = createSignal<string | null>(null);

  const addSectionAction = useAction(addDossierSection);
  const removeItemAction = useAction(removeFromDossier);

  const dossier = createAsync(() => getDossier(params.id || ""));
  const videos = createAsync(() => getVideos());

  const sections = () => dossier()?.sections || [];
  const dossierReady = () => dossier() !== undefined;
  const defaultSectionId = () => sections()[0]?.id ?? null;
  const currentSectionId = () => activeSection() ?? defaultSectionId();

  const handleAddSection = async () => {
    const name = newSectionName().trim();
    if (!name || !params.id) return;
    
    setActionLoading("add-section");
    try {
      const formData = new FormData();
      formData.append("dossierId", params.id);
      formData.append("name", name);
      await addSectionAction(formData);
      setNewSectionName("");
      setShowAddSection(false);
    } catch (error) {
      console.error("Failed to add section:", error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveItem = async (itemId: string) => {
    if (!confirm("Remove this item from the dossier?")) return;
    
    setActionLoading(`remove-${itemId}`);
    try {
      const formData = new FormData();
      formData.append("itemId", itemId);
      await removeItemAction(formData);
    } catch (error) {
      console.error("Failed to remove item:", error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleExport = () => {
    const d = dossier();
    if (!d) return;
    
    // Create a simple JSON export
    const exportData = {
      title: d.title,
      description: d.description,
      exportedAt: new Date().toISOString(),
      sections: d.sections,
      notes: notes(),
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${d.title.replace(/[^a-z0-9]/gi, "_")}_dossier.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getItemLink = (item: { type: string; content: string }) => {
    switch (item.type) {
      case "video":
        return `/video/${item.content}`;
      case "prediction":
        return `/track/predictions/${item.content}`;
      case "analysis":
        return `/investigate/analysis/${item.content}`;
      case "link":
        return item.content; // URL stored in content
      default:
        return null;
    }
  };

  return (
    <AppShell>
      <Title>{dossier()?.title ?? "Dossier"} - IntelAnalyst</Title>
      <Suspense
        fallback={
          <div class="p-6 space-y-3 max-w-3xl mx-auto">
            <Skeleton class="h-8 w-72" />
            <Skeleton class="h-4 w-56" />
            <Skeleton class="h-72 w-full" />
          </div>
        }
      >
        <Show when={dossierReady()}>
          <Show
            when={dossier()}
            fallback={
              <div class="p-6 max-w-2xl mx-auto space-y-4">
                <Breadcrumbs
                  items={[
                    { label: "Investigate", href: "/investigate/dossiers" },
                    { label: "Dossiers", href: "/investigate/dossiers" },
                    { label: "Not Found" },
                  ]}
                  showHome
                  class="mb-2"
                />
                <div class="space-y-2">
                  <h1 class="text-2xl font-bold tracking-tight">Dossier not found</h1>
                  <p class="text-sm text-muted-foreground">
                    This dossier may have been deleted, or you may not have access.
                  </p>
                </div>
                <A href="/investigate/dossiers">
                  <Button variant="outline">Back to Dossiers</Button>
                </A>
              </div>
            }
          >
            {(d) => (
              <>
              <div class="flex h-[calc(100vh-4rem)] overflow-hidden">
                <div class="w-64 border-r flex flex-col bg-muted/30">
                  <div class="p-4 border-b">
                    {/* Breadcrumbs */}
                    <Breadcrumbs
                      items={[
                        { label: "Investigate", href: "/investigate/dossiers" },
                        { label: "Dossiers", href: "/investigate/dossiers" },
                        { label: d().title },
                      ]}
                      showHome
                      class="mb-3"
                    />
                    <h1 class="font-semibold text-lg leading-tight">{d().title}</h1>
                    <p class="text-xs text-muted-foreground mt-1">
                      {d().itemCount} items • Updated {formatRelativeTime(d().updatedAt)}
                    </p>
                  </div>

          <ScrollArea class="flex-1 p-2">
            <div class="space-y-1">
              <p class="px-2 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Sections
              </p>
              <Suspense fallback={<SectionsSkeleton />}>
                <For each={sections()}>
                  {(section) => (
                    <button
                      class={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left transition-colors ${
                        currentSectionId() === section.id
                          ? "bg-primary text-primary-foreground"
                          : "hover:bg-accent"
                      }`}
                      onClick={() => setActiveSection(section.id)}
                    >
                      <GripVertical class="h-3 w-3 opacity-50" />
                      <span class="flex-1">{section.name}</span>
                      <span class="text-xs opacity-70">{section.items.length}</span>
                    </button>
                  )}
                </For>
              </Suspense>
              <Show
                when={showAddSection()}
                fallback={
                  <button 
                    class="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-accent hover:text-foreground"
                    onClick={() => setShowAddSection(true)}
                  >
                    <Plus class="h-3 w-3" />
                    Add Section
                  </button>
                }
              >
                <div class="flex items-center gap-1 px-2 py-1">
                  <Input
                    placeholder="Section name..."
                    class="h-7 text-sm flex-1"
                    value={newSectionName()}
                    onInput={(e) => setNewSectionName(e.currentTarget.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleAddSection();
                      if (e.key === "Escape") {
                        setShowAddSection(false);
                        setNewSectionName("");
                      }
                    }}
                    autofocus
                  />
                  <Button 
                    variant="ghost" 
                    size="icon-sm"
                    onClick={handleAddSection}
                    disabled={actionLoading() === "add-section"}
                  >
                    <Check class="h-3 w-3" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon-sm"
                    onClick={() => {
                      setShowAddSection(false);
                      setNewSectionName("");
                    }}
                  >
                    <X class="h-3 w-3" />
                  </Button>
                </div>
              </Show>
            </div>
          </ScrollArea>

          <div class="p-2 border-t">
            <div class="space-y-1">
              <Button 
                variant="outline" 
                size="sm" 
                class="w-full justify-start"
                onClick={() => setShowAnalysisModal(true)}
              >
                <BarChart3 class="h-4 w-4 mr-2" />
                Analyze Dossier
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                class="w-full justify-start"
                onClick={() => setShowReportModal(true)}
              >
                <FileText class="h-4 w-4 mr-2" />
                Generate Report
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                class="w-full justify-start"
                onClick={handleExport}
              >
                <Download class="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </div>

        <div class="flex-1 flex flex-col overflow-hidden">
          <div class="border-b px-6 py-3 flex items-center justify-between">
            <Show
              when={currentSectionId()}
              fallback={<span class="text-muted-foreground">Select a section</span>}
            >
              <h2 class="font-medium">
                {sections().find((s) => s.id === currentSectionId())?.name}
              </h2>
            </Show>
            <div class="flex gap-2">
              <Button 
                variant={isEditing() ? "default" : "ghost"} 
                size="sm"
                onClick={() => setIsEditing(!isEditing())}
              >
                <Edit class="h-4 w-4 mr-1" />
                {isEditing() ? "Done" : "Edit"}
              </Button>
              <Button size="sm" onClick={() => setShowAddItemModal(true)}>
                <Plus class="h-4 w-4 mr-1" />
                Add Item
              </Button>
            </div>
          </div>

          <ScrollArea class="flex-1 p-6">
            <Show
              when={currentSectionId()}
              fallback={
                <EmptyState
                  icon={<FileText class="h-12 w-12" />}
                  title="Select a section"
                  description="Choose a section from the sidebar to view and edit its contents"
                />
              }
            >
              <div class="space-y-4">
                <For
                  each={sections().find((s) => s.id === currentSectionId())?.items || []}
                  fallback={
                    <EmptyState
                      icon={<Plus class="h-12 w-12" />}
                      title="No items yet"
                      description="Add videos, snippets, or notes to this section"
                      actionLabel="Add Item"
                      onAction={() => setShowAddItemModal(true)}
                    />
                  }
                >
                  {(item) => (
                    <Card class="group">
                      <CardContent class="p-4">
                        <div class="flex items-start gap-3">
                          <GripVertical class="h-4 w-4 mt-1 text-muted-foreground opacity-0 group-hover:opacity-100 cursor-grab" />
                          <div class="flex-1 min-w-0">
                            <Badge variant="outline" class="text-xs mb-2">
                              {item.type}
                            </Badge>
                            <p class="text-sm">{item.content}</p>
                            <p class="text-xs text-muted-foreground mt-2">
                              Added {formatRelativeTime(item.createdAt)}
                            </p>
                          </div>
                          <div class={`flex gap-1 transition-opacity ${isEditing() ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
                            <Show when={getItemLink(item)}>
                              {(link) => (
                                <Show
                                  when={item.type === "link"}
                                  fallback={
                                    <A 
                                      href={link()}
                                      class="inline-flex items-center justify-center rounded-md text-sm font-medium h-7 w-7 hover:bg-accent hover:text-accent-foreground"
                                    >
                                      <ExternalLink class="h-3 w-3" />
                                    </A>
                                  }
                                >
                                  <a 
                                    href={link()}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="inline-flex items-center justify-center rounded-md text-sm font-medium h-7 w-7 hover:bg-accent hover:text-accent-foreground"
                                  >
                                    <ExternalLink class="h-3 w-3" />
                                  </a>
                                </Show>
                              )}
                            </Show>
                            <Show when={isEditing()}>
                              <Button 
                                variant="ghost" 
                                size="icon-sm"
                                onClick={() => handleRemoveItem(item.id)}
                                disabled={actionLoading() === `remove-${item.id}`}
                              >
                                <Trash2 class="h-3 w-3" />
                              </Button>
                            </Show>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </For>
              </div>
            </Show>
          </ScrollArea>
        </div>

        <div class="w-80 border-l flex flex-col bg-muted/30">
          <div class="p-4 border-b">
            <h3 class="font-medium flex items-center gap-2">
              <FileText class="h-4 w-4" />
              Dossier Notes
            </h3>
          </div>
          <div class="flex-1 p-4">
            <Textarea
              placeholder="Add notes about this investigation..."
              class="min-h-[200px] text-sm resize-none"
              value={notes()}
              onInput={(e) => setNotes(e.currentTarget.value)}
              name="dossier-notes"
            />
          </div>

          <Separator />

          <div class="p-4">
            <h3 class="font-medium flex items-center gap-2 mb-3">
              <MessageSquare class="h-4 w-4" />
              What's New
            </h3>
            <Suspense fallback={<Skeleton class="h-24" />}>
              <Show when={videos()?.slice(0, 3)}>
                {(vids) => (
                  <div class="space-y-2">
                    <For each={vids()}>
                      {(video) => (
                        <A href={`/video/${video.id}`} class="block p-2 rounded hover:bg-accent">
                          <p class="text-xs font-medium line-clamp-2">{video.title}</p>
                          <p class="text-xs text-muted-foreground mt-0.5">
                            {video.channel?.name} • {formatRelativeTime(video.publishedAt)}
                          </p>
                        </A>
                      )}
                    </For>
                  </div>
                )}
              </Show>
            </Suspense>
          </div>
                </div>
              </div>

              {/* Add to Dossier Modal - Currently showing for reference, would need a video picker */}
              <AddToDossierModal
                isOpen={showAddItemModal()}
                onClose={() => setShowAddItemModal(false)}
                itemType="video"
                itemId=""
                itemTitle="Select items to add"
                onAdd={(dossierId, sectionId) => {
                  console.log("Added to dossier:", dossierId, "section:", sectionId);
                  setShowAddItemModal(false);
                }}
              />

              {/* Analysis Modal */}
              <NewAnalysisModal
                isOpen={showAnalysisModal()}
                onClose={() => setShowAnalysisModal(false)}
                preselectedVideoIds={[]}
              />

              {/* Report Modal */}
              <GenerateReportModal
                open={showReportModal()}
                onOpenChange={setShowReportModal}
                preselectedSourceType="dossiers"
                preselectedSourceId={params.id}
              />
              </>
            )}
          </Show>
        </Show>
      </Suspense>
    </AppShell>
  );
}

function SectionsSkeleton() {
  return (
    <div class="space-y-1">
      <For each={[1, 2, 3, 4]}>
        {() => <Skeleton class="h-9 w-full" />}
      </For>
    </div>
  );
}
